import { useNavigate } from 'react-router-dom';
import { usePendingTasks } from '../hooks/useTasks';
import { CheckCircleIcon, ClockIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { supabase } from '../supabaseClient';
import toast from 'react-hot-toast';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationsPanel({ isOpen, onClose }: NotificationsPanelProps) {
  const { tasks, loading, reload } = usePendingTasks();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleTaskClick = async (task: any) => {
    // Mark task as in_progress when clicked
    try {
      await supabase
        .from('tasks')
        .update({ status: 'in_progress' })
        .eq('id', task.id);
      
      reload();
    } catch (error) {
      console.error('Error updating task:', error);
    }

    // Navigate to contract
    navigate(`/contracts/${task.contractId}`);
    onClose();
  };

  const handleMarkAllRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('tasks')
        .update({ status: 'in_progress' })
        .eq('assigned_to', user.id)
        .eq('status', 'pending');

      toast.success('All notifications marked as read');
      reload();
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark as read');
    }
  };

  const getTaskTypeIcon = (taskType: string) => {
    switch (taskType) {
      case 'review':
        return <CheckCircleIcon className="w-5 h-5 text-blue-500" />;
      case 'sign':
        return <DocumentTextIcon className="w-5 h-5 text-purple-500" />;
      case 'approve':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-30" 
        onClick={onClose}
      />

      {/* Notifications Panel */}
      <div className="absolute right-0 mt-2 w-screen max-w-[calc(100vw-1rem)] sm:w-96 sm:max-w-none -mr-2 sm:mr-0 bg-white rounded-lg shadow-xl border border-gray-200 z-40 max-h-[32rem] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {tasks.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Mark all as read
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12 px-4">
              <CheckCircleIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-900 mb-1">All caught up!</p>
              <p className="text-xs text-gray-500">No pending tasks at the moment</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {tasks.map((task: any) => (
                <button
                  key={task.id}
                  onClick={() => handleTaskClick(task)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-50"
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getTaskTypeIcon(task.taskType)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {task.title}
                      </p>
                      {task.description && (
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="capitalize">{task.taskType}</span>
                        <span>•</span>
                        <span>{getRelativeTime(task.createdAt)}</span>
                        {task.priority !== 'normal' && (
                          <>
                            <span>•</span>
                            <span className={`font-medium ${
                              task.priority === 'urgent' ? 'text-red-600' :
                              task.priority === 'high' ? 'text-orange-600' :
                              'text-gray-500'
                            }`}>
                              {task.priority}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {tasks.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-center text-gray-500">
              {tasks.length} pending task{tasks.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>
    </>
  );
}

